import { useState, useEffect, useMemo } from 'react';
import { teamApi } from '../../api/users.js';
import { leadsApi } from '../../api/leads.js';
import { visitsApi } from '../../api/visits.js';

function displayRole(roleName) {
  const map = { sales_rep: 'Salesman', sales_tl: 'Team Leader', admin: 'Admin' };
  if (map[roleName]) return map[roleName];
  if (!roleName) return '—';
  return roleName
    .split(/_|-/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function TeamStaffsPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await teamApi.getMembers();
        const list = res.data ?? [];

        const withCounts = await Promise.all(list.map(async (m) => {
          const [leadsRes, visitsRes] = await Promise.all([
            leadsApi.getAll({ sales_id: m.id, limit: 1 }).catch(() => null),
            visitsApi.getAll({ sales_id: m.id, limit: 1 }).catch(() => null),
          ]);
          return {
            id: m.id,
            name: m.name,
            email: m.email,
            phone: m.phone,
            role: m.role?.name,
            areas: m.areas || [],
            isActive: m.is_active !== false,
            customerCount: leadsRes?.count ?? 0,
            visitCount: visitsRes?.data?.count ?? 0,
          };
        }));

        if (!cancelled) setMembers(withCounts);
      } catch (err) {
        console.error('Failed to load team:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m =>
      m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    );
  }, [members, search]);

  return (
    <div className="page-body">
      <div className="card">
        <div className="card-header">
          <span className="card-title">My Team</span>
        </div>
        <div className="card-body">
          <div className="filters-row">
            <div className="search-bar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input placeholder="Search team…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Areas</th>
                  <th>Status</th>
                  <th>Assigned Customers</th>
                  <th>Visits</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Loading…</td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>No team members found.</td>
                  </tr>
                ) : filtered.map(m => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 600 }}>{m.name}</td>
                    <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{m.email}</td>
                    <td style={{ fontSize: 13 }}>{m.phone || '—'}</td>
                    <td>
                      {m.areas.length > 0 ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {m.areas.map(a => (
                            <span key={a.id} className="badge badge-gray" style={{ fontSize: 11 }}>{a.name}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>None</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${m.isActive ? 'success' : 'gray'}`}>{m.isActive ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{m.customerCount}</td>
                    <td style={{ fontWeight: 600 }}>{m.visitCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
